/**
 * Paths that are steps in the auth flow rather than pages to send a signed-in user back to.
 *
 * `/logout` signs the user out the moment it renders, so treating it as a destination is
 * self-defeating: capturing it as `from` (or replaying it as `return_to`) is what turned a
 * single sign-out into an endless loop — the login round-trip landed the brand-new session
 * straight back on `/logout`, which revoked it about a second later, and the user was asked
 * for their password again. And again.
 *
 * `/auth/*` is the backend's OAuth plumbing plus the SPA's post-callback router; neither is
 * somewhere a user should be parked.
 */
const NON_RETURNABLE_PATHS = ['/logout', '/auth'];

/**
 * Whether a signed-in user can be sent to this path after authenticating.
 *
 * Mirrors `isSafeReturnTo` on the backend, which is the authoritative gate — this is the
 * client-side half so the SPA never captures or forwards a destination the backend would
 * refuse anyway. Compares the path only; callers pass `location.pathname`.
 */
export function isReturnablePath(pathname: string): boolean {
  return !NON_RETURNABLE_PATHS.some(
    (nonReturnable) => pathname === nonReturnable || pathname.startsWith(`${nonReturnable}/`),
  );
}
